const domain = import.meta.env.VITE_REACT_APP_API_SERVER_URL;
const projectSlug = import.meta.env.VITE_PROJECT_SLUG;
import { defaultLocale } from "@src/utils/i18n";
import { usePersistentSurveyStore } from "@src/store/survey";

/**
 * Get survey sections
 * @returns {Array} - Array of survey sections
 */
export async function getSurveySections() {
  if (!projectSlug) {
    throw new Error("projectSlug is required");
  }
  const locale = usePersistentSurveyStore.getState().language;
  let url;
  if (locale !== defaultLocale) {
    url = `${domain}/api/project_surveys/${projectSlug}?language=${locale}`;
  } else {
    url = `${domain}/api/project_surveys/${projectSlug}`;
  }
  const result = await fetch(url, {
    method: "GET",
    mode: "cors",
  });
  if (result.ok) {
    return await result.json();
  }
  throw new Error(result.statusText);
}

export async function fetchSurveySection(surveySectionSlug) {
  if (!surveySectionSlug) {
    throw new Error("surveySectionSlug is required");
  }
  const locale = usePersistentSurveyStore.getState().language;
  let url;
  if (!locale || locale !== defaultLocale) {
    url = `${domain}/api/survey/${surveySectionSlug}/full?language=${locale}`;
  } else {
    url = `${domain}/api/survey/${surveySectionSlug}/full`;
  }
  const result = await fetch(url, {
    method: "GET",
    mode: "cors",
  });
  if (result.ok) {
    return await result.json();
  } else {
    throw new Error(result.statusText);
  }
}

/**
 * Record a survey session
 * @param {String} sessionUUID - UUID of the session
 * @param {String} userUUID - UUID of the user
 * @param {String} surveySectionSlug - Slug of the survey section
 */
export async function recordSession({
  sessionUUID,
  userUUID,
  surveySectionSlug,
}) {
  const result = await fetch(`${domain}/api/survey/session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    mode: "cors",
    body: JSON.stringify({
      uuid: sessionUUID,
      user_uuid: userUUID,
      survey_slug: surveySectionSlug,
    }),
  });

  if (result.ok) {
    return await result.json();
  } else {
    throw new Error(result.statusText);
  }
}

/**
 * Record a survey response
 * @param {String} answerUUID - UUID of the answer
 * @param {String} sessionUUID - UUID of the session
 * @param {Object} response - Response to the question
 * @param {String} surveyQuestionUUID - UUID of the survey question
 */
export async function recordResponse({
  answerUUID,
  sessionUUID,
  response,
  surveyQuestionUUID,
}) {
  if (!response) {
    return;
  }
  const result = await fetch(`${domain}/api/survey/response`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    mode: "cors",
    body: JSON.stringify({
      uuid: answerUUID,
      session_uuid: sessionUUID,
      response,
      survey_question_uuid: surveyQuestionUUID,
    }),
  });

  if (result.ok) {
    return await result.json();
  } else {
    throw new Error(result.statusText);
  }
}

/**
 * Record a survey event
 * @param {String} eventType - Type of the event
 * @param {String} sessionUUID - UUID of the session
 * @param {Object} eventData - Data of the event
 * @param {String} surveySectionSlug - Slug of the survey section
 */
export async function recordEvent({
  eventType,
  sessionUUID,
  eventData,
  surveySectionSlug,
}) {
  if (!eventData) {
    return;
  }
  const result = await fetch(`${domain}/api/survey/event`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    mode: "cors",
    body: JSON.stringify({
      event_type: eventType,
      session_uuid: sessionUUID,
      event_data: eventData,
      survey_slug: surveySectionSlug,
    }),
  });

  if (result.ok) {
    return await result.json();
  } else {
    throw new Error(result.statusText);
  }
}
